import { addTokenToUrl, ApiClient } from "@/services/apiClient";
import { type Image } from "@/services/images";
import { type AxiosResponse } from "axios";
import { EventBus, Event } from "@/services/eventBus";
import { gettext } from "@/services/translation";
import { GalleryApiError } from "@/services/collection/item/gallery/exceptions";
import { type CollectionItem } from "@/services/collection/item";
import type { AiIdentification } from "@/services/aiIdentification";

type GenericGalleryResponse =
  | {
      status: "error";
      message: string;
    }
  | {
      status: "ok";
      imageId: number;
    };

type Response_Upload = GenericGalleryResponse;
type Response_Remove = GenericGalleryResponse;
type Response_MakeCover = GenericGalleryResponse;

export const uploadImage = async (file: File, item: CollectionItem): Promise<Response_Upload> => {
  const url = addTokenToUrl("collection/gallery/upload");
  const data = new FormData();
  data.append("image", file);
  data.append("itemId", item.id.toString());

  return ApiClient.post<Response_Upload>(url, {}, data).then((response) => {
    handleResponse(response.data, gettext.$gettext("Fotka přidána."));
    return response.data;
  });
};

export const setImagesFromAiIdentification = async (item: CollectionItem, identification: AiIdentification): Promise<AxiosResponse> => {
  const url = addTokenToUrl("collection/gallery/set-from-ai-identification");
  const data = {
    identificationId: identification.id,
    itemId: item.id,
  };
  return ApiClient.post(url, {}, data);
};

export const removeImage = async (item: CollectionItem, image: Image): Promise<Response_Remove> => {
  const url = addTokenToUrl("collection/gallery/remove/" + image.id);
  return ApiClient.get<Response_Remove>(url).then((response) => {
    handleResponse(response.data, gettext.$gettext("Smazal(a) jsi fotku."));
    item.images = item.images.filter((i) => i.id !== image.id);
    return response.data;
  });
};

export const makeCover = async (item: CollectionItem, newCoverImage: Image): Promise<Response_MakeCover> => {
  const url = addTokenToUrl("collection/gallery/make-cover/" + newCoverImage.id);
  return ApiClient.get<Response_MakeCover>(url).then(({ data: responseData }) => {
    handleResponse(responseData, gettext.$gettext("Nastaveno jako cover."));

    const i = item.images.findIndex((i) => i.id === newCoverImage.id);
    if (i < 0) {
      throw new Error("DEV: Should not happen.");
    }
    const [img] = item.images.splice(i, 1);
    item.images.unshift(img);

    return responseData;
  });
};

/** Helper to handle old-style api response. */
function handleResponse(response: GenericGalleryResponse, okMsg: string): void {
  const status = response.status;
  if (status === "ok") {
    EventBus.emit({ event: Event.ToastSuccess, data: okMsg });
  } else {
    const msg = response.message;
    EventBus.emit({ event: Event.ToastError, data: msg });
    throw new GalleryApiError();
  }
}

export function catchGalleryApiError(e: unknown): void {
  if (e instanceof GalleryApiError) {
    return;
  } else {
    throw e;
  }
}
